import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  NAME_MAX,
  formatPriceInput,
  getPrice,
  hasTwoDecimals,
  isVisible,
  parsePrice,
  validateIngredient,
} from "./ingredientUtils";

// No celular o diálogo vira "bottom sheet" (preso embaixo, entra de baixo)
const SHEET_CLASSES =
  "max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:left-0 max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-3xl max-sm:border-x-0 max-sm:border-b-0 max-sm:pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:slide-in-from-bottom-full max-sm:data-[state=closed]:slide-out-to-left-0 max-sm:data-[state=closed]:slide-out-to-bottom-full max-sm:data-[state=open]:zoom-in-100 max-sm:data-[state=closed]:zoom-out-100";

const inputClass =
  "h-11 w-full rounded-xl border bg-card px-3.5 text-base text-foreground transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 disabled:opacity-60 md:text-sm";

const EMPTY = { name: "", price: "", visible: true };

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 text-xs font-medium text-destructive" role="alert">
      {message}
    </p>
  );
}

/**
 * Criar/editar ingrediente. `ingredient` null = criação.
 * `ingredients` (lista completa) serve para barrar nome repetido.
 */
function IngredientFormModal({ open, onOpenChange, ingredient, ingredients = [], onSave, loading }) {
  const [formData, setFormData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const nameRef = useRef(null);
  const isEdit = Boolean(ingredient);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setFormData(
      ingredient
        ? {
            name: ingredient.name ?? "",
            price: formatPriceInput(getPrice(ingredient)),
            visible: isVisible(ingredient),
          }
        : EMPTY
    );
  }, [ingredient, open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    const others = ingredients.filter((i) => String(i.id) !== String(ingredient?.id));
    const { errors: nextErrors, payload } = validateIngredient(formData, others);
    setErrors(nextErrors);
    if (!payload) {
      if (nextErrors.name) nameRef.current?.focus();
      return;
    }
    onSave(isEdit ? { ...payload, id: String(ingredient.id) } : payload);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent
        className={cn("max-h-[90vh] max-w-md gap-0 overflow-y-auto rounded-2xl bg-background p-6", SHEET_CLASSES)}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          nameRef.current?.focus();
        }}
      >
        <div className="mx-auto -mt-2 mb-4 h-1.5 w-12 rounded-full bg-border sm:hidden" aria-hidden="true" />
        <DialogHeader className="space-y-0.5 pb-4 pr-8 text-left">
          <DialogTitle className="font-display text-lg font-bold tracking-tight text-foreground">
            {isEdit ? "Editar Ingrediente" : "Novo Ingrediente"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit ? "Atualize os dados do ingrediente." : "Adicione um ingrediente ao catálogo."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {isEdit && (
            <div>
              <Label htmlFor="ingredient-id" className="mb-1 block text-xs font-semibold">
                ID
              </Label>
              <input
                id="ingredient-id"
                value={ingredient.id ?? ""}
                readOnly
                tabIndex={-1}
                className="h-11 w-full cursor-not-allowed rounded-xl border border-border bg-surface-container px-3.5 font-mono text-sm text-muted-foreground"
              />
            </div>
          )}

          <div>
            <Label htmlFor="ingredient-name" className="mb-1 block text-xs font-semibold">
              Nome <span className="text-destructive">*</span>
            </Label>
            <input
              id="ingredient-name"
              ref={nameRef}
              value={formData.name}
              maxLength={NAME_MAX}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Ex.: Queijo prato"
              autoComplete="off"
              disabled={loading}
              aria-invalid={Boolean(errors.name) || undefined}
              aria-describedby={errors.name ? "ingredient-name-error" : undefined}
              className={cn(inputClass, "font-medium", errors.name ? "border-destructive" : "border-border")}
            />
            <FieldError id="ingredient-name-error" message={errors.name} />
          </div>

          <div>
            <Label htmlFor="ingredient-price" className="mb-1 block text-xs font-semibold">
              Preço (R$) <span className="text-destructive">*</span>
            </Label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">
                R$
              </span>
              <input
                id="ingredient-price"
                inputMode="decimal"
                value={formData.price}
                onChange={(e) => handleChange("price", e.target.value.replace(/[^\d,.]/g, ""))}
                onBlur={() => {
                  const value = parsePrice(formData.price);
                  if (Number.isFinite(value) && hasTwoDecimals(value)) {
                    handleChange("price", formatPriceInput(value));
                  }
                }}
                placeholder="0,00"
                disabled={loading}
                aria-invalid={Boolean(errors.price) || undefined}
                aria-describedby="ingredient-price-hint"
                className={cn(inputClass, "pl-10 font-semibold", errors.price ? "border-destructive" : "border-border")}
              />
            </div>
            {errors.price ? (
              <FieldError id="ingredient-price-hint" message={errors.price} />
            ) : (
              <p id="ingredient-price-hint" className="mt-1 text-[11px] text-muted-foreground">
                Valor cobrado como adicional. Use vírgula ou ponto (ex.: 2,50); 0 para gratuito.
              </p>
            )}
          </div>

          <label
            htmlFor="ingredient-visible"
            className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-card/80 p-3"
          >
            <span className="pr-1">
              <span className="block text-xs font-semibold text-foreground">Exibir no catálogo</span>
              <span className="mt-0.5 block text-[11px] leading-tight text-muted-foreground">
                Desative para ocultar temporariamente o ingrediente dos adicionais.
              </span>
            </span>
            <Switch
              id="ingredient-visible"
              checked={formData.visible}
              onCheckedChange={(checked) => handleChange("visible", checked)}
              disabled={loading}
            />
          </label>

          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="h-10 rounded-full border border-border bg-surface-container px-5 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-6 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95 disabled:opacity-70"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Salvando..." : isEdit ? "Atualizar" : "Criar"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default IngredientFormModal;
